import {
  getCallRailFallbackFields,
  getName,
  getSafeCallRailSessionId,
} from "./helpers";
import { getToday } from "../../utils/helpers";

/**
 * GRAVITY FORMS
 */
export const sendToGravityForms = async (
  formData = {},
  verificationData = {},
  formId = "",
) => {
  const restUrl = `/wp-json/gf/v2/forms/${formId}/submissions`;
  const currentHref = window?.location?.href || "";

  const name = getName(formData);

  try {
    const res = await fetch(restUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": CVFPluginGravityFormURL.nonce,
      },
      body: JSON.stringify({
        input_1: JSON.stringify({
          name,
          relationship_to_patient: formData?.relationship_to_patient,
          email: formData?.email,
          phone_number: formData?.phone_number,
          preferred_contact_method: formData?.contact_method,
          zip_code: formData?.zip_code,
          patient_age: formData?.patient_age,
          payer_name: formData?.payer_name,
          is_subscriber_patient:
            formData?.is_subscriber_patient === "true" ? "Yes" : "No",
          verification_response_message:
            verificationData?.data?.APIResponseMessage,
          coverage_status: verificationData?.data?.CoverageStatus,
          verification_type: verificationData?.data?.VerificationType || "",
          is_provider_in_network: verificationData?.data?.IsProviderInNetwork,
          message: formData?.message,
          referral_source: formData?.referral_source,
          submission_page: currentHref,
        }),
      }),
    });
    if (!res.ok) {
      console.error(res);
      console.error(res.status);
      return;
    }

    const json = await res.json();
    return json;
  } catch (err) {
    console.error(err);
    return;
  }
};

/**
 * CALLRAIL
 */
export const sendToCallrail = async (formData, verificationData) => {
  const callrailSessionId = getSafeCallRailSessionId();
  const currentHref = window?.location?.href || "";
  const name = getName(formData);

  let requiredInfo = {};

  if (callrailSessionId) {
    requiredInfo = { session_id: callrailSessionId };
  } else {
    requiredInfo = getCallRailFallbackFields();
  }

  try {
    const res = await fetch("/wp-json/cvf/v1/callrail-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        form_submission: {
          form_name: "Coverage Verification Form",
          form_url:
            typeof window !== "undefined" && window.location?.href
              ? window.location.href
              : "/form-url",
          ...requiredInfo,
          form_data: {
            ["Name"]: name,
            ["Form Name"]: "Coverage Verification Form",
            ["Phone Number"]: formData?.phone_number,
            ["Email"]: formData?.email,
            ["Insurance Provider"]: formData?.payer_name,
            ["Patient DOB"]: formData?.patient_dob,
            ["Patient Age"]: formData?.patient_age,
            ["Relationship to Patient"]: formData?.relationship_to_patient,
            ["How can we help?"]: formData?.message,
            ["Prefered Contact Method"]: formData?.contact_method,
            ["Zipcode"]: formData?.zip_code,
            ["How did you hear about us?"]: formData?.referral_source,
            ["SMS Opt In"]: formData?.sms_opt_in,
            ["Is subscriber patient?"]:
              formData?.is_subscriber_patient === "true" ? "Yes" : "No",
            ["Verification Response Message"]:
              verificationData?.data?.APIResponseMessage,
            ["Coverage Status"]: verificationData?.data?.CoverageStatus,
            ["Verification Type"]:
              verificationData?.data?.VerificationType || "",
            ["Is provider in network?"]:
              verificationData?.data?.IsProviderInNetwork,
            ["Submission Page"]: currentHref,
          },
        },
      }),
    });
    if (!res.ok) {
      setError("root.serverError", {
        message: `Server error (${res.status}). Please try again.`,
      });
      return;
    }
    const json = await res.json();
    return json;
  } catch (err) {
    console.error(err);
    return;
  }
};

/**
 * CALL TRACKING METRICS
 */
export const sendToCtm = async (formData, verificationData) => {
  const ctm_visitor_sid = window?.__ctm?.config?.sid || "";
  const currentHref = window?.location?.href || "";

  const name = getName(formData);

  try {
    const res = await fetch("/wp-json/cvf/v1/ctm-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone_number: formData?.phone_number,
        country_code: "+1",
        caller_name: name,
        email: formData?.email,
        visitor_sid: ctm_visitor_sid,
        custom_provider: formData?.payer_name,
        custom_patient_age: formData?.patient_age,
        custom_patient_dob: formData?.patient_dob,
        custom_message: formData?.message,
        custom_relationship_to_patient: formData?.relationship_to_patient,
        custom_prefered_contact_method: formData?.contact_method,
        custom_sms_opt_in: formData?.sms_opt_in,
        //
        custom_is_subscriber_patient:
          formData?.is_subscriber_patient === "true" ? "Yes" : "No",
        custom_verification_response_message:
          verificationData?.data?.APIResponseMessage,
        custom_coverage_status: verificationData?.data?.CoverageStatus,
        custom_verification_type:
          verificationData?.data?.VerificationType || "",
        custom_is_provider_in_network:
          verificationData?.data?.IsProviderInNetwork,
        custom_referral_source: formData?.referral_source,
        custom_submission_page: currentHref,
      }),
    });
    if (!res.ok) {
      throw new Error(`Server error (${res.status}). Please try again.`);
    }
    const json = await res.json();
    return json;
  } catch (err) {
    console.error(err);
    return;
  }
};

/** Storage in WP Endpoint */
export const submitWordpressStorage = async (formData) => {
  try {
    const response = await fetch("/wp-json/cvf/v1/store-verification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Request failed");
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error("Server returned an error");
    }

    return data;
  } catch (error) {
    console.error("Verification storage failed:", error);
    throw error; // rethrow so your UI can react (toast, error state, etc.)
  }
};

// VERIFICATION FUNCTION
export async function verifyInsurance(formData) {
  const today = getToday();
  const providerName =
    window?.CVFPluginData?.options?.pverify_provider_name || "";
  const providerNPI =
    window?.CVFPluginData?.options?.pverify_provider_npi || "";

  const dependent =
    formData?.is_subscriber_patient === "false"
      ? {
          patient: {
            firstName: formData?.patient_first_name,
            lastName: formData?.patient_last_name,
            dob: formData?.patient_dob,
          },
        }
      : null;

  try {
    const response = await fetch("/wp-json/cvf/v1/pverify/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payerCode: formData?.payer_id,
        payerName: formData?.payer_name,
        provider: {
          firstName: "",
          middleName: "",
          lastName: providerName,
          npi: providerNPI,
        },
        subscriber: {
          firstName: formData?.subscriber_first_name,
          dob: formData?.patient_dob,
          lastName: formData?.subscriber_last_name,
          memberID: formData?.member_id,
        },
        dependent,
        isSubscriberPatient: formData?.is_subscriber_patient,
        doS_StartDate: today,
        doS_EndDate: today,
        Location: "Any location Name",
        IncludeTextResponse: "true",
      }),
    });

    // Handle non-2xx HTTP responses
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: "Unknown server error" };
      }

      throw new Error(
        errorData?.message || `Request failed with status ${response.status}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Eligibility verification failed:", error);

    // Re-throw so calling code can decide how to handle it
    throw error;
  }
}

export const formTracking = () => {
  if (window?.CE2 && window?.CE2?.goalConversion) {
    window.CE2.goalConversion({
      name: "Coverage Verification Form Submit",
      value: 1,
    });
  }
  if (window?.gtag) {
    window.gtag("event", "coverage_verification_form_submit", {
      form_name: "coverage_verification",
      form_type: "insurance",
      success: true,
    });
  }
  return;
};
